import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';
import Title from '../../components/Title';
import EditModal from '../../components/EditModal';
import { useAccount } from '../../context/AccountContext';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL } from '../../common/AppConstants';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';


const TITLE = 'Profile';

const userColumns = [
  {
    "attr": "email",
    "label": "Email",
    "type": "text",
    "related": false,
    "required": true,
    "editable": false,
    "extra": false,
    "help_text": "Email"
  },
  {
    "attr": "first_name",
    "label": "First Name",
    "type": "text",
    "related": false,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "First Name"
  },
  {
    "attr": "last_name",
    "label": "Last Name",
    "type": "text",
    "related": false,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Last Name"
  },
  {
    "attr": "phone",
    "label": "Phone",
    "type": "text",
    "related": false,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Phone number (e.g +919876543210)"
  },
];

export default function Profile() {
  const { addNotification } = useNotification();
  const { account, updateUser } = useAccount();
  const [isUserEditModalOpen, setIsUserEditModalOpen] = useState(false);

  const handleUserEditModalClose = () => {
    setIsUserEditModalOpen(false);
  };

  const handleUserEditSave = async (updatedEditRow, onEditFailure) => {
    try {
      const response = await updateUser(updatedEditRow);
      if (response.status === 200) {
        handleUserEditModalClose();
      } else {
        onEditFailure(response.data);
      }
    } catch (error) {
      console.error('Failed to update user - ', error);
      addNotification(API_ERROR_MESSAGE.networkError, 'error');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 1,
          }}
        >
          <Title>{TITLE}</Title>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          mt: 2,
        }}
      >
        {/* Profile Content */}
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            },
            mb: 2,
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4">Profile</Typography>
                {/* More data chips if needed */}
              </Box>
            }
            action={
              <IconButton onClick={() => setIsUserEditModalOpen(true)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{ pb: 2 }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Name:</Typography> {account?.user.first_name} {account?.user.last_name}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Email:</Typography> {account?.user.email ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Phone:</Typography> {account?.user.phone ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Role:</Typography> {account?.user.current_role ?? NA_LABEL}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* User Edit Modal */}
        <Box>
          <EditModal
            open={isUserEditModalOpen}
            columns={userColumns}
            rowData={account?.user}
            relatedData={{}}
            handleClose={handleUserEditModalClose}
            handleSave={handleUserEditSave}
            edit_title="Edit User"
          />
        </Box>
      </Box>
    </Box>
  );
}
